import React, { useCallback, useEffect, useMemo, useState } from "react";
import { getCurrentUser } from "../utils/auth";

const API_BASE = process.env.REACT_APP_API_URL || "https://api.portpilot.co";

function formatTimestamp(value) {
  if (!value) return "—";
  const iso = /\d{4}-\d{2}-\d{2}T/.test(value) ? value : `${value.replace(/\s+/, "T")}Z`;
  const dt = new Date(iso);
  if (Number.isNaN(dt.getTime())) return value;
  return dt.toLocaleString();
}

function formatEvent(event) {
  if (!event) return "—";
  const lower = String(event).toLowerCase();
  if (lower === "login") return "Login";
  if (lower === "logout") return "Logout";
  return event;
}

export default function AdminLogs() {
  const me = getCurrentUser();
  const [rows, setRows] = useState([]);
  const [jobRows, setJobRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [jobLoading, setJobLoading] = useState(false);
  const [jobError, setJobError] = useState("");

  const loadLogs = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const headers = { "Content-Type": "application/json" };
      if (me?.email) headers["X-User-Email"] = me.email;
      if (me?.role) headers["X-User-Role"] = me.role;
      const resp = await fetch(`${API_BASE}/auth/logs`, { headers });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok || !data.ok) throw new Error(data.error || "Fetch failed");
      setRows(Array.isArray(data.rows) ? data.rows : []);
    } catch (err) {
      console.error(err);
      setError(err.message || "Unable to load logs.");
    } finally {
      setLoading(false);
    }
  }, [me?.email, me?.role]);

  const loadJobLogs = useCallback(async () => {
    setJobLoading(true);
    setJobError("");
    try {
      const headers = { "Content-Type": "application/json" };
      if (me?.email) headers["X-User-Email"] = me.email;
      if (me?.role) headers["X-User-Role"] = me.role;
      const resp = await fetch(`${API_BASE}/job-logs?limit=100`, { headers });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok || !data.ok) throw new Error(data.error || "Fetch failed");
      setJobRows(Array.isArray(data.rows) ? data.rows : []);
    } catch (err) {
      console.error(err);
      setJobError(err.message || "Unable to load job logs.");
    } finally {
      setJobLoading(false);
    }
  }, [me?.email, me?.role]);

  useEffect(() => {
    loadLogs();
    loadJobLogs();
  }, [loadLogs, loadJobLogs]);

  const displayRows = useMemo(() => rows.slice(), [rows]);
  const displayJobRows = useMemo(() => jobRows.slice(), [jobRows]);

  return (
    <div className="p-4 space-y-4">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-xl font-bold">Authentication Log</h1>
          <p className="text-sm text-slate-600">
            Login and logout events recorded over the past 60 days.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={loadLogs}
            disabled={loading}
            className="rounded-md border px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-60"
          >
            {loading ? "Refreshing..." : "Refresh"}
          </button>
          <button
            type="button"
            onClick={loadJobLogs}
            disabled={jobLoading}
            className="rounded-md border px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-60"
          >
            {jobLoading ? "Refreshing Jobs..." : "Refresh Jobs"}
          </button>
        </div>
      </div>

      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="overflow-auto border rounded-md">
        <table className="min-w-full border-collapse text-sm">
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              <th className="px-3 py-2 text-left font-semibold">When</th>
              <th className="px-3 py-2 text-left font-semibold">Event</th>
              <th className="px-3 py-2 text-left font-semibold">Email</th>
              <th className="px-3 py-2 text-left font-semibold">Role</th>
              <th className="px-3 py-2 text-left font-semibold">IP</th>
              <th className="px-3 py-2 text-left font-semibold">User Agent</th>
            </tr>
          </thead>
          <tbody>
            {loading && displayRows.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-3 py-6 text-center text-slate-500">
                  Loading logs...
                </td>
              </tr>
            ) : displayRows.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-3 py-6 text-center text-slate-500">
                  No activity recorded yet.
                </td>
              </tr>
            ) : (
              displayRows.map((row, idx) => {
                const key =
                  row.created_at && row.email
                    ? `${row.email}-${row.created_at}`
                    : `authlog-${idx}`;
                return (
                <tr key={key}>
                  <td className="border-t px-3 py-2 align-top text-slate-700">{formatTimestamp(row.created_at)}</td>
                  <td className="border-t px-3 py-2 align-top">{formatEvent(row.event)}</td>
                  <td className="border-t px-3 py-2 align-top">{row.email || "-"}</td>
                  <td className="border-t px-3 py-2 align-top">{row.role || "-"}</td>
                  <td className="border-t px-3 py-2 align-top">{row.ip || "-"}</td>
                  <td className="border-t px-3 py-2 align-top">{row.user_agent || "-"}</td>
                </tr>
              );
              })
            )}
          </tbody>
        </table>
      </div>

      <div className="space-y-2">
        <div>
          <h2 className="text-lg font-semibold">Job Runs</h2>
          <p className="text-sm text-slate-600">Cron jobs and batch executions recorded recently.</p>
        </div>
        {jobError && (
          <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {jobError}
          </div>
        )}
        <div className="overflow-auto border rounded-md">
          <table className="min-w-full border-collapse text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="px-3 py-2 text-left font-semibold">Job</th>
                <th className="px-3 py-2 text-left font-semibold">Started</th>
                <th className="px-3 py-2 text-left font-semibold">Finished</th>
                <th className="px-3 py-2 text-left font-semibold">Status</th>
                <th className="px-3 py-2 text-left font-semibold">Processed</th>
                <th className="px-3 py-2 text-left font-semibold">Stored</th>
                <th className="px-3 py-2 text-left font-semibold">Skipped</th>
                <th className="px-3 py-2 text-left font-semibold">Failed</th>
                <th className="px-3 py-2 text-left font-semibold">Message</th>
              </tr>
            </thead>
            <tbody>
              {jobLoading && displayJobRows.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-3 py-6 text-center text-slate-500">
                    Loading job logs...
                  </td>
                </tr>
              ) : displayJobRows.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-3 py-6 text-center text-slate-500">
                    No job activity recorded yet.
                  </td>
                </tr>
              ) : (
                displayJobRows.map((row, idx) => {
                  const key =
                    row.started_at && row.job_name
                      ? `${row.job_name}-${row.started_at}`
                      : `joblog-${idx}`;
                  return (
                  <tr key={key}>
                    <td className="border-t px-3 py-2 align-top">{row.job_name || "-"}</td>
                    <td className="border-t px-3 py-2 align-top">{formatTimestamp(row.started_at)}</td>
                    <td className="border-t px-3 py-2 align-top">{formatTimestamp(row.finished_at)}</td>
                    <td className="border-t px-3 py-2 align-top">{row.status || "-"}</td>
                    <td className="border-t px-3 py-2 align-top">{row.processed ?? 0}</td>
                    <td className="border-t px-3 py-2 align-top">{row.stored ?? 0}</td>
                    <td className="border-t px-3 py-2 align-top">{row.skipped ?? 0}</td>
                    <td className="border-t px-3 py-2 align-top">{row.failed ?? 0}</td>
                    <td className="border-t px-3 py-2 align-top">{row.message || "-"}</td>
                  </tr>
                );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
